/**
 * ForestryFlight - Flight Planner Main Component
 * Professional drone flight planning for forestry operations
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';

import MapInterface from './components/MapInterface';
import { AREA_METHODS, UTM_GRID_SIZES } from './components/AreaSelectionMethod';
import WorkflowNavButtons from './components/WorkflowNavButtons';
import WorkflowFlightType from './components/WorkflowFlightType';
import WorkflowDrawing from './components/WorkflowDrawing';
import WorkflowSetup from './components/WorkflowSetup';
import WorkflowExport from './components/WorkflowExport';
import WorkflowDspConfig from './components/WorkflowDspConfig';
import LayersPanel from './components/LayersPanel';
import {
	PageContainer,
	Header,
	HeaderLeft,
	BackLink,
	Title,
	StepBadges,
	StepBadge,
	MainContent,
	LeftSidebar,
	MapContainer,
	RightSidebar,
} from './components/FlightPlannerStyles';
import { mergeGridSquares } from './utils/utmGrid';
import { LAT_TO_METERS, lngToMetersAtLat } from './utils/coordinateUtils';
import { calculatePolygonArea, formatArea } from './utils/geometry';
import { downloadKML } from './utils/kmlExport';
import {
	calculateDSPAltitude,
	computeDSPPattern,
	computeDSPWaypoints,
	computeAutoCoverage,
	computeFlightPattern,
} from './utils/flightCalculations';
import { useReverseGeocode } from './hooks/useReverseGeocode';
import {
	WORKFLOW_STATES,
	DRONE_MODELS,
	DSP_LENSES,
	DSP_TEMPLATES,
	DEFAULT_FLIGHT_TEMPLATES,
} from './constants/flightConstants';

// Import API utilities for AI models
import { getAvailableModels } from '@utils/api/models';
import {
	createFlightPlan,
	updateFlightPlan,
	getFlightTemplates,
	getFlightPlan,
} from '@utils/api/forestryFlight';

// ─── Initial state defaults ──────────────────────────────────────────────────
// Extracted as constants so that useState() and handleNewMission() share a single
// source of truth. If you add a new key, it only needs to change in one place.

const INITIAL_DSP_SETTINGS = {
	template: 'sapling-count',
	lens: 'wide',
	plotArea: 15, // m² - area of each DSP plot
	pointCount: 10,
	distributionMethod: 'even',
	minimumDistance: 50,
	maximumDistance: 200, // Maximum distance between DSPs for area coverage
	alignmentAngle: 0, // 0 = North-South, 90 = East-West
	speed: 5, // m/s flight speed between DSPs
	terrainFollow: true, // DSP typically uses terrain following
	gimbalPitch: -90, // Gimbal angle: -90° = straight down (default for DSP)
	maxOverlap: 20, // Maximum overlap percentage for auto-coverage (negative = gap/buffer)
	stopAtDsp: true, // Whether drone stops at each DSP point
	stopDuration: 3, // Seconds to hover at each DSP point
	heightMeasurementEnabled: false, // Enable height measurement data collection at each DSP
};

const INITIAL_PROJECT_SETTINGS = {
	name: '',
	description: '',
	municipality: '',
	propertyOwner: '',
	droneModel: '',
	flightTemplate: 'ungskogpleie',
	aiModelUuid: '', // AI model to run after ODM processing
	// Flight settings (populated from template on selection).
	// These defaults are overridden when a template is selected.
	// TODO(verify-with-PO): confirm default values match intended "no template" state.
	flightHeight: 40, // meters AGL
	sideOverlap: 80, // percentage
	frontOverlap: 85, // percentage
	speed: 5, // m/s
	terrainFollow: true, // metadata only — not used in flight pattern calc
	smartOblique: true, // metadata only — not used in flight pattern calc
	gimbalPitch: -90, // metadata only — not used in flight pattern calc
	flightAngle: 0, // degrees: 0 = North-South, 90 = East-West
};

const FlightPlanner = () => {
	const [workflowState, setWorkflowState] = useState(WORKFLOW_STATES.DRAWING);
	const [polygonPoints, setPolygonPoints] = useState([]);
	const [flightType, setFlightType] = useState(null);
	const [waypoints, setWaypoints] = useState([]);
	const [flightLines, setFlightLines] = useState([]);
	const [isDrawingActive, setIsDrawingActive] = useState(false);
	const [flightTemplates, setFlightTemplates] = useState(DEFAULT_FLIGHT_TEMPLATES);

	// Area selection method state
	const [areaMethod, setAreaMethod] = useState(AREA_METHODS.DRAW);
	const [utmGridSize, setUtmGridSize] = useState('500m');
	const [selectedGridSquares, setSelectedGridSquares] = useState([]);
	const [importedFileName, setImportedFileName] = useState(null);
	const [gridSearchTarget, setGridSearchTarget] = useState(null);

	// Airspace layer visibility state
	const [airspaceLayers, setAirspaceLayers] = useState({
		airports: true,
		military: true,
		heliports: true,
		government: true,
		nature: false,
		property: false,
		customMap: false,
	});

	// Layer opacity state for WMS layers
	const [layerOpacity, setLayerOpacity] = useState({
		nature: 0.6,
		property: 0.8,
		customMap: 0.7,
	});

	// Custom map configuration (configurable per customer)
	// TODO: Get this config in a proper way, keeping the credentials in code-base is a big 'NO'
	const [customMapConfig] = useState({
		url: 'https://kart.allskog.no/arcgis/rest/services/Ekstern/Allskog_BiodroneFlightplanner',
		username: 'Biodrone',
		password: 'BiodroneFlightplanner',
		layerName: 'Allskog',
	});

	// Line width for property boundaries (1-4 range to avoid clutter)
	const [propertyLineWidth, setPropertyLineWidth] = useState(2);

	const [dspSettings, setDspSettings] = useState(INITIAL_DSP_SETTINGS);
	const [dspFootprints, setDspFootprints] = useState([]); // Image footprints for each DSP
	const [isAddingDsp, setIsAddingDsp] = useState(false); // Manual DSP placement mode
	const [startingPointIndex, setStartingPointIndex] = useState(0); // Index of starting waypoint
	const [projectSettings, setProjectSettings] = useState(INITIAL_PROJECT_SETTINGS);
	const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
	const [showDspSettings, setShowDspSettings] = useState(false); // Toggle for DSP advanced settings
	const [showDspAdvanced, setShowDspAdvanced] = useState(false); // Toggle for DSP advanced options (auto coverage, distance constraints)
	const [showDistanceConstraints, setShowDistanceConstraints] = useState(false); // Toggle for min/max distance
	const [flightStats, setFlightStats] = useState(null);
	const [measurements, setMeasurements] = useState([]); // Measurement results from map
	const [availableAiModels, setAvailableAiModels] = useState([]); // AI models for auto-detection
	const [editingPlanId, setEditingPlanId] = useState(null); // ID of plan being edited (null = new plan)
	const [fitTrigger, setFitTrigger] = useState(0); // Increment to trigger map zoom-to-polygon

	// Export handlers
	const [isSaving, setIsSaving] = useState(false);
	const [saveMessage, setSaveMessage] = useState(null);

	// Auto-generate DSP waypoints when entering DSP_CONFIG or when settings change (debounced)
	const dspRegenerateTimerRef = useRef(null);

	// Auto-detect municipality from polygon center (debounced, cancellable)
	const detectedMunicipality = useReverseGeocode(polygonPoints);

	// Sync detected municipality into project settings
	useEffect(() => {
		if (detectedMunicipality) {
			setProjectSettings(prev => ({
				...prev,
				municipality: detectedMunicipality.code || detectedMunicipality.name,
			}));
		}
	}, [detectedMunicipality]);

	// Fetch flight templates from API
	useEffect(() => {
		const fetchTemplates = async () => {
			try {
				const data = await getFlightTemplates();
				// Convert API templates to the format used by the component.
				// Supports both flat fields and nested flight_settings object.
				const templatesMap = {};
				data.forEach(t => {
					// Skip DSP-only templates — they don't apply to orthomosaic workflow
					if (t.template_type === 'dsp') return;

					const fs = t.flight_settings || {};
					templatesMap[t.id] = {
						name: t.name,
						description: t.description,
						requireMunicipality: t.require_municipality ?? true,
						settings: {
							flightHeight: fs.flight_height ?? t.flight_height,
							sideOverlap: fs.side_overlap ?? t.side_overlap,
							frontOverlap: fs.front_overlap ?? t.front_overlap,
							speed: fs.speed ?? t.speed,
							terrainFollow: fs.terrain_follow ?? t.terrain_follow,
							smartOblique: fs.smart_oblique ?? t.smart_oblique,
							gimbalPitch: fs.gimbal_pitch ?? t.gimbal_pitch,
							aiModelUuid: t.ai_model_uuid || '',
						},
					};
				});
				if (Object.keys(templatesMap).length > 0) {
					setFlightTemplates(templatesMap);
				}
			} catch (err) {
				console.warn('Failed to fetch flight templates, using defaults:', err);
			}
		};
		fetchTemplates();
	}, []);

	// Fetch available AI models from main BioDrone API
	useEffect(() => {
		const fetchModels = async () => {
			try {
				const models = await getAvailableModels();
				const mapped = models.map(m => ({
					uuid: m.model_uuid,
					name: m.description || m.model_uuid,
					image_mode: m.image_mode,
				}));
				setAvailableAiModels(mapped);
			} catch (err) {
				console.warn('Failed to fetch AI models:', err);
			}
		};
		fetchModels();
	}, []);

	// Calculate DSP footprints and flight lines
	const calculateDSPPattern = useCallback(() => {
		const { footprints, lines } = computeDSPPattern({
			waypoints,
			plotArea: dspSettings.plotArea,
			lensId: dspSettings.lens,
			alignmentAngle: dspSettings.alignmentAngle,
		});
		setDspFootprints(footprints);
		setFlightLines(lines);
	}, [waypoints, dspSettings.plotArea, dspSettings.lens, dspSettings.alignmentAngle]);

	// Recalculate DSP pattern when settings change
	useEffect(() => {
		if (flightType === 'dsp') {
			calculateDSPPattern();
		}
	}, [flightType, calculateDSPPattern]);

	// Handle DSP template change
	const handleDSPTemplateChange = useCallback(templateId => {
		const template = DSP_TEMPLATES[templateId];
		if (template) {
			setDspSettings(prev => ({
				...prev,
				template: templateId,
				plotArea: template.defaultArea,
				lens: template.recommendedLens,
			}));
		}
	}, []);

	// Generate DSP waypoints within polygon
	const generateDSPWaypoints = useCallback(() => {
		const result = computeDSPWaypoints({
			polygonPoints,
			dspSettings,
			showDistanceConstraints,
		});
		setWaypoints(result);
	}, [
		polygonPoints,
		dspSettings.pointCount,
		dspSettings.distributionMethod,
		dspSettings.minimumDistance,
		dspSettings.maximumDistance,
		dspSettings.alignmentAngle,
		showDistanceConstraints,
	]);

	// Auto-coverage: Generate waypoints to cover entire area with specified max overlap
	const generateAutoCoverage = useCallback(() => {
		const { waypoints: newWaypoints, pointCount } = computeAutoCoverage({
			polygonPoints,
			plotArea: dspSettings.plotArea,
			maxOverlap: dspSettings.maxOverlap,
			alignmentAngle: dspSettings.alignmentAngle,
		});
		setWaypoints(newWaypoints);
		setDspSettings(prev => ({
			...prev,
			pointCount,
			distributionMethod: 'even',
		}));
	}, [polygonPoints, dspSettings.plotArea, dspSettings.maxOverlap, dspSettings.alignmentAngle]);

	useEffect(() => {
		if (workflowState === WORKFLOW_STATES.DSP_CONFIG && polygonPoints.length >= 3) {
			if (dspSettings.distributionMethod !== 'manual') {
				clearTimeout(dspRegenerateTimerRef.current);
				dspRegenerateTimerRef.current = setTimeout(() => {
					generateDSPWaypoints();
				}, 300);
			}
		}
		return () => clearTimeout(dspRegenerateTimerRef.current);
	}, [
		workflowState,
		dspSettings.pointCount,
		dspSettings.distributionMethod,
		dspSettings.minimumDistance,
		dspSettings.maximumDistance,
		dspSettings.alignmentAngle,
		generateDSPWaypoints,
	]);

	// Calculate flight pattern and statistics
	const calculateFlightPattern = useCallback(() => {
		const result = computeFlightPattern({
			polygonPoints,
			droneModelId: projectSettings.droneModel,
			flightHeight: projectSettings.flightHeight,
			sideOverlap: projectSettings.sideOverlap,
			frontOverlap: projectSettings.frontOverlap,
			speed: projectSettings.speed,
			flightAngle: projectSettings.flightAngle,
		});
		if (result) {
			setFlightLines(result.lines);
			setFlightStats(result.stats);
		} else {
			setFlightStats(null);
			setFlightLines([]);
		}
	}, [
		polygonPoints,
		projectSettings.droneModel,
		projectSettings.flightHeight,
		projectSettings.sideOverlap,
		projectSettings.frontOverlap,
		projectSettings.speed,
		projectSettings.flightAngle,
	]);

	// Recalculate when settings change
	useEffect(() => {
		if (workflowState === WORKFLOW_STATES.SETUP || workflowState === WORKFLOW_STATES.EXPORT) {
			calculateFlightPattern();
		}
	}, [workflowState, calculateFlightPattern]);

	const getStepNumber = state => {
		const steps = [
			WORKFLOW_STATES.DRAWING,
			WORKFLOW_STATES.FLIGHT_TYPE,
			WORKFLOW_STATES.DSP_CONFIG,
			WORKFLOW_STATES.SETUP,
			WORKFLOW_STATES.EXPORT,
		];
		return steps.indexOf(state) + 1;
	};

	const isStepCompleted = state => {
		const currentStep = getStepNumber(workflowState);
		const checkStep = getStepNumber(state);
		return checkStep < currentStep;
	};

	const isStepActive = state => workflowState === state;

	const handleFlightTypeSelect = type => {
		setFlightType(type);
	};

	const handlePolygonChange = useCallback(points => {
		setPolygonPoints(points);
	}, []);

	const handleDrawingComplete = useCallback(() => {
		setIsDrawingActive(false);
	}, []);

	const handleStartDrawing = useCallback(() => {
		// Clear existing polygon when starting new drawing
		setPolygonPoints([]);
		setIsDrawingActive(true);
	}, []);

	// Area method handlers
	const handleAreaMethodChange = useCallback(method => {
		setAreaMethod(method);
		// Reset state when switching methods
		if (method !== AREA_METHODS.UTM_GRID) {
			setSelectedGridSquares([]);
			setGridSearchTarget(null);
		}
		if (method !== AREA_METHODS.IMPORT_FILE) {
			setImportedFileName(null);
		}
		if (method !== AREA_METHODS.DRAW) {
			setIsDrawingActive(false);
		}
	}, []);

	const handleGridSquareToggle = useCallback(square => {
		setSelectedGridSquares(prev => {
			const exists = prev.find(s => s.id === square.id);
			let updated;
			if (exists) {
				updated = prev.filter(s => s.id !== square.id);
			} else {
				updated = [...prev, square];
			}
			// Merge selected squares into polygon points
			if (updated.length > 0) {
				const merged = mergeGridSquares(updated);
				setPolygonPoints(merged);
			} else {
				setPolygonPoints([]);
			}
			return updated;
		});
	}, []);

	const handleFileImport = useCallback((points, fileName) => {
		setPolygonPoints(points);
		setImportedFileName(fileName);
		setFitTrigger(prev => prev + 1); // Zoom to imported polygon
	}, []);

	const handleClearImport = useCallback(() => {
		setImportedFileName(null);
		setPolygonPoints([]);
	}, []);

	const handleClearGridSelection = useCallback(() => {
		setSelectedGridSquares([]);
		setPolygonPoints([]);
	}, []);

	const handleWaypointsChange = useCallback(
		newWaypoints => {
			// After dragging a waypoint, re-optimize the route order
			// so the flight path stays logical with the new positions
			if (newWaypoints.length > 2 && flightType === 'dsp') {
				const centerLat = newWaypoints[0].latitude;
				const lngToMeters = lngToMetersAtLat(centerLat);

				const sorted = [newWaypoints[0]];
				const remaining = newWaypoints.slice(1);

				while (remaining.length > 0) {
					const last = sorted[sorted.length - 1];
					let nearestIdx = 0;
					let nearestDist = Infinity;
					for (let i = 0; i < remaining.length; i++) {
						const dx = (remaining[i].longitude - last.longitude) * lngToMeters;
						const dy = (remaining[i].latitude - last.latitude) * LAT_TO_METERS;
						const dist = dx * dx + dy * dy;
						if (dist < nearestDist) {
							nearestDist = dist;
							nearestIdx = i;
						}
					}
					sorted.push(remaining[nearestIdx]);
					remaining.splice(nearestIdx, 1);
				}
				setWaypoints(sorted);
			} else {
				setWaypoints(newWaypoints);
			}
		},
		[flightType]
	);

	// Delete a single measurement
	const handleDeleteMeasurement = useCallback(measurementId => {
		setMeasurements(prev => prev.filter(m => m.id !== measurementId));
	}, []);

	// Optimize waypoint order using nearest-neighbor algorithm
	const optimizeWaypointRoute = useCallback(() => {
		if (waypoints.length <= 2) return;

		const centerLat = waypoints[0].latitude;
		const lngToMeters = lngToMetersAtLat(centerLat);

		const sorted = [waypoints[0]];
		const remaining = waypoints.slice(1);

		while (remaining.length > 0) {
			const last = sorted[sorted.length - 1];
			let nearestIdx = 0;
			let nearestDist = Infinity;

			for (let i = 0; i < remaining.length; i++) {
				const dx = (remaining[i].longitude - last.longitude) * lngToMeters;
				const dy = (remaining[i].latitude - last.latitude) * LAT_TO_METERS;
				const dist = dx * dx + dy * dy;
				if (dist < nearestDist) {
					nearestDist = dist;
					nearestIdx = i;
				}
			}

			sorted.push(remaining[nearestIdx]);
			remaining.splice(nearestIdx, 1);
		}

		setWaypoints(sorted);
	}, [waypoints]);

	const handleContinue = () => {
		switch (workflowState) {
			case WORKFLOW_STATES.DRAWING:
				setWorkflowState(WORKFLOW_STATES.FLIGHT_TYPE);
				break;
			case WORKFLOW_STATES.FLIGHT_TYPE:
				if (flightType === 'dsp') {
					setWorkflowState(WORKFLOW_STATES.DSP_CONFIG);
				} else {
					setWorkflowState(WORKFLOW_STATES.SETUP);
				}
				break;
			case WORKFLOW_STATES.DSP_CONFIG:
				// Skip SETUP for DSP mode, go straight to EXPORT
				setWorkflowState(WORKFLOW_STATES.EXPORT);
				break;
			case WORKFLOW_STATES.SETUP:
				setWorkflowState(WORKFLOW_STATES.EXPORT);
				break;
			default:
				break;
		}
	};

	const handleBack = () => {
		switch (workflowState) {
			case WORKFLOW_STATES.FLIGHT_TYPE:
				setWorkflowState(WORKFLOW_STATES.DRAWING);
				break;
			case WORKFLOW_STATES.DSP_CONFIG:
				setWorkflowState(WORKFLOW_STATES.FLIGHT_TYPE);
				break;
			case WORKFLOW_STATES.SETUP:
				setWorkflowState(WORKFLOW_STATES.FLIGHT_TYPE);
				break;
			case WORKFLOW_STATES.EXPORT:
				if (flightType === 'dsp') {
					// Go back to DSP_CONFIG for DSP mode
					setWorkflowState(WORKFLOW_STATES.DSP_CONFIG);
				} else {
					setWorkflowState(WORKFLOW_STATES.SETUP);
				}
				break;
			default:
				break;
		}
	};

	/**
	 * Reset all flight planner state to initial values.
	 * Called when the user clicks "New Flight Plan" to start a fresh mission.
	 *
	 * IMPORTANT: Every piece of state that contributes to map layer rendering
	 * (polygon, waypoints, flight lines, DSP footprints, measurements, starting point)
	 * must be explicitly cleared here. Otherwise the previous plan's visuals will
	 * persist on the map even though the workflow resets to Step 1.
	 */
	const handleNewMission = () => {
		// --- Workflow & drawing state ---
		setWorkflowState(WORKFLOW_STATES.DRAWING);
		setIsDrawingActive(false);
		setAreaMethod(AREA_METHODS.DRAW);
		setEditingPlanId(null);
		setImportedFileName(null);
		setSelectedGridSquares([]);

		// --- Map layer data (clearing these triggers layer sync effects in MapInterface) ---
		setPolygonPoints([]);
		setWaypoints([]);
		setFlightLines([]);
		setDspFootprints([]); // Must clear explicitly — no effect auto-clears when flightType becomes null
		setMeasurements([]);
		setStartingPointIndex(0);

		// --- Flight configuration (use shared constants to avoid drift) ---
		setFlightType(null);
		setProjectSettings(INITIAL_PROJECT_SETTINGS);
		setDspSettings(INITIAL_DSP_SETTINGS);

		// --- UI panel toggles ---
		setShowAdvancedSettings(false);
		setShowDspSettings(false);
		setShowDspAdvanced(false);
		setShowDistanceConstraints(false);
		setIsAddingDsp(false);

		// --- Computed / transient state ---
		setFlightStats(null);
		setIsSaving(false);
		setSaveMessage(null);
	};

	// Load a saved flight plan into the editor
	const handleLoadPlan = useCallback(async plan => {
		try {
			// Fetch full plan data from API
			const fullPlan = await getFlightPlan(plan.id);

			// Convert GeoJSON polygon back to [{lat, lng}]
			const coords = fullPlan.polygon_geojson?.coordinates?.[0] || [];
			// Remove closing point (GeoJSON repeats first point at end)
			const polyPoints = coords.slice(0, -1).map(c => ({ lat: c[1], lng: c[0] }));

			// Convert waypoints
			const wps = (fullPlan.waypoints || []).map(wp => ({
				latitude: wp.latitude,
				longitude: wp.longitude,
			}));

			// Set polygon
			setPolygonPoints(polyPoints);

			// Set flight type
			const ft = fullPlan.flight_type || 'orthomosaic';
			setFlightType(ft);

			// Set editing plan ID
			setEditingPlanId(fullPlan.id);

			if (ft === 'dsp') {
				// Restore DSP settings
				const dsp = fullPlan.dsp_settings || {};
				setDspSettings(prev => ({
					...prev,
					template: dsp.template || prev.template,
					lens: dsp.lens || 'wide',
					plotArea: dsp.plot_area || 15,
					pointCount: dsp.point_count || wps.length || 10,
					distributionMethod: dsp.distribution_method || 'even',
					minimumDistance: dsp.minimum_distance || 50,
					maximumDistance: dsp.maximum_distance || 200,
					alignmentAngle: dsp.alignment_angle || 0,
					speed: dsp.speed || 5,
					terrainFollow: dsp.terrain_follow ?? true,
					gimbalPitch: dsp.gimbal_pitch ?? -90,
					maxOverlap: dsp.max_overlap ?? 20,
					stopAtDsp: dsp.stop_at_dsp ?? true,
					stopDuration: dsp.stop_duration || 3,
					heightMeasurementEnabled: dsp.height_measurement_enabled || false,
				}));
				// Set waypoints from saved plan
				setWaypoints(wps);
				// Jump to DSP config step
				setWorkflowState(WORKFLOW_STATES.DSP_CONFIG);
			} else {
				// Restore orthomosaic flight settings
				const fs = fullPlan.flight_settings || {};
				setProjectSettings(prev => ({
					...prev,
					droneModel: fullPlan.drone_model || '',
					flightTemplate: fullPlan.flight_template || prev.flightTemplate,
					flightHeight: fs.flight_height || 80,
					sideOverlap: fs.side_overlap || 80,
					frontOverlap: fs.front_overlap || 85,
					speed: fs.speed || 5,
					gimbalPitch: fs.gimbal_pitch ?? -90,
					terrainFollow: fs.terrain_follow ?? false,
					smartOblique: fs.smart_oblique ?? true,
					flightAngle: fs.flight_angle || 0,
				}));
				// Jump to setup step
				setWorkflowState(WORKFLOW_STATES.SETUP);
			}

			// Common settings
			setProjectSettings(prev => ({
				...prev,
				name: fullPlan.name || prev.name,
				description: fullPlan.description || prev.description,
				municipality: fullPlan.municipality || prev.municipality,
				aiModelUuid: fullPlan.ai_model_uuid || prev.aiModelUuid,
			}));

			setIsDrawingActive(false);
			setFlightLines([]);
			setSaveMessage(null);
			setFitTrigger(prev => prev + 1);
		} catch (err) {
			console.error('Failed to load plan:', err);
			setSaveMessage({ type: 'error', text: `Failed to load plan: ${err.message}` });
		}
	}, []);

	const handleSaveFlightRoute = async () => {
		if (!polygonPoints || polygonPoints.length < 3) {
			setSaveMessage({ type: 'error', text: 'Please draw a flight area first' });
			return;
		}
		if (!projectSettings.name?.trim()) {
			setSaveMessage({ type: 'error', text: 'Project name is required' });
			return;
		}

		setIsSaving(true);
		setSaveMessage(null);

		try {
			let exportWaypoints = [];
			if (flightType === 'dsp') {
				const computedAltitude = calculateDSPAltitude(
					dspSettings.plotArea,
					dspSettings.lens
				);
				exportWaypoints = waypoints.map(wp => ({
					latitude: wp.latitude,
					longitude: wp.longitude,
					altitude: computedAltitude || 50,
				}));
			} else {
				flightLines.forEach(segment => {
					segment.forEach(point => {
						exportWaypoints.push({
							latitude: point.lat,
							longitude: point.lng,
							altitude: projectSettings.flightHeight || 80,
						});
					});
				});
			}

			const planData = {
				name: projectSettings.name,
				description: projectSettings.description || '',
				flight_type: flightType || 'orthomosaic',
				drone_model:
					flightType === 'dsp' ? 'dji-matrice-4-enterprise' : projectSettings.droneModel,
				flight_template: projectSettings.flightTemplate,
				polygon_points: polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })),
				waypoints: exportWaypoints,
				municipality: projectSettings.municipality || null,
				ai_model_uuid: projectSettings.aiModelUuid || null,
				dsp_settings:
					flightType === 'dsp'
						? {
								method: 'manual',
								minimum_distance: dspSettings.minimumDistance || 50,
								maximum_distance: dspSettings.maximumDistance || 200,
								point_size: 10,
								lens: dspSettings.lens || 'wide',
								plot_area: dspSettings.plotArea || 15,
								speed: dspSettings.speed || 5,
								gimbal_pitch: dspSettings.gimbalPitch ?? -90,
								terrain_follow: dspSettings.terrainFollow ?? true,
								alignment_angle: dspSettings.alignmentAngle || 0,
								max_overlap: dspSettings.maxOverlap ?? 20,
								stop_at_dsp: dspSettings.stopAtDsp ?? true,
								stop_duration: dspSettings.stopDuration || 3,
								height_measurement_enabled:
									dspSettings.heightMeasurementEnabled || false,
								lrf_enabled: true,
								template: dspSettings.template || null,
							}
						: null,
				flight_settings:
					flightType === 'orthomosaic'
						? {
								flight_height: projectSettings.flightHeight || 80,
								side_overlap: projectSettings.sideOverlap || 80,
								front_overlap: projectSettings.frontOverlap || 85,
								speed: projectSettings.speed || 5,
								gimbal_pitch: projectSettings.gimbalPitch ?? -90,
								terrain_follow: projectSettings.terrainFollow ?? true,
								smart_oblique: projectSettings.smartOblique ?? true,
								flight_angle: projectSettings.flightAngle || 0,
							}
						: null,
			};

			if (editingPlanId) {
				// Update existing plan
				await updateFlightPlan(editingPlanId, planData);
				setSaveMessage({
					type: 'success',
					text: '✓ Flight plan updated! Changes synced to Android app.',
				});
			} else {
				// Create new plan
				const result = await createFlightPlan(planData);
				await updateFlightPlan(result.id, { status: 'saved' });
				setEditingPlanId(result.id);
				setSaveMessage({
					type: 'success',
					text: '✓ Flight route saved! Project is ready in BioDrone Portal and available in the Android app.',
				});
			}
		} catch (error) {
			console.error('Save error:', error);
			setSaveMessage({ type: 'error', text: `Save failed: ${error.message}` });
		} finally {
			setIsSaving(false);
		}
	};

	const handleDownloadKML = () => {
		if (!polygonPoints || polygonPoints.length < 3) {
			setSaveMessage({ type: 'error', text: 'Please draw a flight area first' });
			return;
		}

		downloadKML({
			name: projectSettings.name,
			polygonPoints,
			waypoints,
			flightHeight: projectSettings.flightHeight || 80,
		});
	};

	// Handle template change - apply template settings
	const handleTemplateChange = templateId => {
		const template = flightTemplates[templateId];
		if (template) {
			setProjectSettings(prev => ({
				...prev,
				flightTemplate: templateId,
				...template.settings,
			}));
		}
	};

	const renderRightPanel = () => {
		switch (workflowState) {
			case WORKFLOW_STATES.DRAWING:
				return (
					<>
						<WorkflowDrawing
							areaMethod={areaMethod}
							onAreaMethodChange={handleAreaMethodChange}
							utmGridSize={utmGridSize}
							onUtmGridSizeChange={setUtmGridSize}
							onGridSearch={setGridSearchTarget}
							importedFileName={importedFileName}
							onFileImport={handleFileImport}
							onClearImport={handleClearImport}
							polygonPoints={polygonPoints}
							isDrawingActive={isDrawingActive}
							onStartDrawing={handleStartDrawing}
							selectedGridSquares={selectedGridSquares}
							onClearGridSelection={handleClearGridSelection}
							formattedArea={formatArea(calculatePolygonArea(polygonPoints))}
						/>

						<WorkflowNavButtons
							backLabel="← Back to Tools"
							backTo="/tools"
							onContinue={handleContinue}
							continueDisabled={polygonPoints.length < 3}
						/>
					</>
				);

			case WORKFLOW_STATES.FLIGHT_TYPE:
				return (
					<>
						<WorkflowFlightType
							flightType={flightType}
							onFlightTypeSelect={handleFlightTypeSelect}
						/>

						<WorkflowNavButtons
							onBack={handleBack}
							onContinue={handleContinue}
							continueDisabled={!flightType}
						/>
					</>
				);

			case WORKFLOW_STATES.DSP_CONFIG:
				return (
					<>
						<WorkflowDspConfig
							projectSettings={projectSettings}
							onProjectSettingsChange={setProjectSettings}
							detectedMunicipality={detectedMunicipality}
							dspSettings={dspSettings}
							onDspSettingsChange={setDspSettings}
							waypoints={waypoints}
							polygonPoints={polygonPoints}
							isAddingDsp={isAddingDsp}
							onToggleAddingDsp={val =>
								setIsAddingDsp(typeof val === 'boolean' ? val : !isAddingDsp)
							}
							onClearWaypoints={() => setWaypoints([])}
							onOptimizeRoute={optimizeWaypointRoute}
							availableAiModels={availableAiModels}
							showDspSettings={showDspSettings}
							onToggleDspSettings={() => setShowDspSettings(!showDspSettings)}
							showDspAdvanced={showDspAdvanced}
							onToggleDspAdvanced={() => setShowDspAdvanced(!showDspAdvanced)}
							showDistanceConstraints={showDistanceConstraints}
							onToggleDistanceConstraints={() =>
								setShowDistanceConstraints(!showDistanceConstraints)
							}
							calculateDSPAltitude={calculateDSPAltitude}
							onGenerateAutoCoverage={generateAutoCoverage}
							onDSPTemplateChange={handleDSPTemplateChange}
						/>

						<WorkflowNavButtons
							onBack={handleBack}
							onContinue={handleContinue}
							continueDisabled={!projectSettings.name}
						/>
					</>
				);

			case WORKFLOW_STATES.SETUP:
				return (
					<>
						<WorkflowSetup
							projectSettings={projectSettings}
							onSettingsChange={setProjectSettings}
							detectedMunicipality={detectedMunicipality}
							flightTemplates={flightTemplates}
							onTemplateChange={handleTemplateChange}
							flightStats={flightStats}
							availableAiModels={availableAiModels}
							showAdvancedSettings={showAdvancedSettings}
							onToggleAdvancedSettings={() =>
								setShowAdvancedSettings(!showAdvancedSettings)
							}
						/>

						<WorkflowNavButtons
							onBack={handleBack}
							onContinue={handleContinue}
							// Municipality is auto-detected via reverse geocoding and included in
							// the save payload as `municipality || null`.
							continueDisabled={!projectSettings.name || !projectSettings.droneModel}
						/>
					</>
				);

			case WORKFLOW_STATES.EXPORT:
				return (
					<>
						<WorkflowExport
							projectSettings={projectSettings}
							flightType={flightType}
							dspSettings={dspSettings}
							dspLensName={DSP_LENSES[dspSettings.lens]?.name}
							waypointCount={waypoints.length}
							formattedArea={formatArea(calculatePolygonArea(polygonPoints))}
							dspAltitude={calculateDSPAltitude(
								dspSettings.plotArea,
								dspSettings.lens
							).toFixed(1)}
							droneName={
								flightType === 'dsp'
									? 'DJI Matrice 4 Enterprise'
									: DRONE_MODELS.find(d => d.id === projectSettings.droneModel)
											?.name || 'Not specified'
							}
							isSaving={isSaving}
							saveMessage={saveMessage}
							editingPlanId={editingPlanId}
							onSave={handleSaveFlightRoute}
							onDownloadKML={handleDownloadKML}
						/>

						<WorkflowNavButtons
							muted
							backLabel="← Edit Settings"
							onBack={handleBack}
							secondaryLabel="+ New Flight"
							onSecondary={handleNewMission}
						/>
					</>
				);

			default:
				return null;
		}
	};

	return (
		<PageContainer>
			<Header>
				<HeaderLeft>
					<BackLink to="/tools">← Back to Tools</BackLink>
					<Title>
						Forestry Flight
						{editingPlanId ? ` — ${projectSettings.name || 'Editing'}` : ''}
					</Title>
				</HeaderLeft>
				<StepBadges>
					<StepBadge
						$active={isStepActive(WORKFLOW_STATES.DRAWING)}
						$completed={isStepCompleted(WORKFLOW_STATES.DRAWING)}>
						1. Area
					</StepBadge>
					<StepBadge
						$active={isStepActive(WORKFLOW_STATES.FLIGHT_TYPE)}
						$completed={isStepCompleted(WORKFLOW_STATES.FLIGHT_TYPE)}>
						2. Type
					</StepBadge>
					{flightType === 'dsp' && (
						<StepBadge
							$active={isStepActive(WORKFLOW_STATES.DSP_CONFIG)}
							$completed={isStepCompleted(WORKFLOW_STATES.DSP_CONFIG)}>
							3. DSP
						</StepBadge>
					)}
					{flightType !== 'dsp' && (
						<StepBadge
							$active={isStepActive(WORKFLOW_STATES.SETUP)}
							$completed={isStepCompleted(WORKFLOW_STATES.SETUP)}>
							3. Setup
						</StepBadge>
					)}
					<StepBadge
						$active={isStepActive(WORKFLOW_STATES.EXPORT)}
						$completed={isStepCompleted(WORKFLOW_STATES.EXPORT)}>
						4. Export
					</StepBadge>
				</StepBadges>
				<Button variant="outline-secondary" size="sm" onClick={handleNewMission}>
					New Flight Plan
				</Button>
			</Header>

			<MainContent>
				<LeftSidebar>
					<LayersPanel
						airspaceLayers={airspaceLayers}
						onAirspaceLayersChange={setAirspaceLayers}
						layerOpacity={layerOpacity}
						onLayerOpacityChange={setLayerOpacity}
						propertyLineWidth={propertyLineWidth}
						onPropertyLineWidthChange={setPropertyLineWidth}
						customMapConfig={customMapConfig}
						measurements={measurements}
						onDeleteMeasurement={handleDeleteMeasurement}
						editingPlanId={editingPlanId}
						onLoadPlan={handleLoadPlan}
					/>
				</LeftSidebar>

				<MapContainer>
					<MapInterface
						polygonPoints={polygonPoints}
						onPolygonChange={handlePolygonChange}
						waypoints={waypoints}
						onWaypointsChange={handleWaypointsChange}
						flightLines={flightLines}
						dspFootprints={dspFootprints}
						drawingEnabled={
							workflowState === WORKFLOW_STATES.DRAWING &&
							isDrawingActive &&
							areaMethod === AREA_METHODS.DRAW
						}
						polygonEditEnabled={
							polygonPoints.length >= 3 && areaMethod === AREA_METHODS.DRAW
						}
						onDrawingComplete={handleDrawingComplete}
						waypointMode={workflowState === WORKFLOW_STATES.DSP_CONFIG && !isAddingDsp}
						manualDspMode={isAddingDsp}
						alignmentAngle={dspSettings.alignmentAngle}
						startingPointIndex={startingPointIndex}
						onStartingPointChange={setStartingPointIndex}
						onMeasurementsChange={setMeasurements}
						measurements={measurements}
						airspaceLayers={airspaceLayers}
						layerOpacity={layerOpacity}
						propertyLineWidth={propertyLineWidth}
						customMapConfig={customMapConfig}
						fitTrigger={fitTrigger}
						utmGridEnabled={
							workflowState === WORKFLOW_STATES.DRAWING &&
							areaMethod === AREA_METHODS.UTM_GRID
						}
						utmGridSize={UTM_GRID_SIZES.find(s => s.id === utmGridSize)?.meters || 1000}
						selectedGridSquares={selectedGridSquares}
						onGridSquareToggle={handleGridSquareToggle}
						gridSearchTarget={gridSearchTarget}
					/>
				</MapContainer>

				<RightSidebar>{renderRightPanel()}</RightSidebar>
			</MainContent>
		</PageContainer>
	);
};

export default FlightPlanner;
